/**
 * Session Management Utility
 *
 * Manages temporary vs persistent sessions for security on shared computers.
 * Uses dual-storage approach:
 * - sessionStorage: Cleared when browser closes
 * - localStorage: Persists to detect browser restarts
 */

export const SessionManager = {
  /**
   * Mark the current session as temporary (will be cleared on browser close)
   */
  markAsTemporary(): void {
    sessionStorage.setItem('temp_session', 'true');
    localStorage.setItem('was_temp_session', 'true');
  },

  /**
   * Mark the current session as persistent (survives browser close)
   */
  markAsPersistent(): void {
    sessionStorage.removeItem('temp_session');
    localStorage.removeItem('was_temp_session');
  },

  /**
   * Check if the session should be terminated
   * Returns true if:
   * - Session was marked as temporary AND
   * - Browser was closed and reopened (sessionStorage cleared)
   */
  shouldTerminate(): boolean {
    const wasTempSession = localStorage.getItem('was_temp_session') === 'true';
    const currentTempFlag = sessionStorage.getItem('temp_session') === 'true';

    // If it was temp but flag is gone, browser was closed
    return wasTempSession && !currentTempFlag;
  },

  /**
   * Clean up session flags after termination
   */
  cleanup(): void {
    localStorage.removeItem('was_temp_session');
    sessionStorage.removeItem('temp_session');
  },

  /**
   * Check if current session is temporary
   */
  isTemporary(): boolean {
    return sessionStorage.getItem('temp_session') === 'true';
  }
};
